/**
 * Who may sign in to this console, and what they may do on each cohort.
 *
 * 🔴 THIS PAGE EXISTS BECAUSE A REFUSAL NAMED A MECHANISM THAT DID NOT. A teacher of several
 * cohorts in Classroom signed in and was told *"ask whoever administers this console to add
 * you"* — correctly, because teaching a cohort in Classroom grants nothing here. But nothing
 * in any route created a teacher; the seed file was the only door and it needs a commit and a
 * deploy.
 *
 * 🔴 THE GRID IS THE PAGE. Capabilities are per (teacher, cohort), so the thing a person needs
 * to see is a matrix and not a list — a per-teacher detail screen would make "who can release
 * on the live cohort?" a question you answer by opening every row.
 */
import { useState } from "react";

import {
  addTeacher, getAdminTeachers, setTeacherActive, setTeacherCapabilities,
  type AdminTeacher, type ApiError,
} from "../api";
import { Badge, Card, CardBody, CardHead, ErrorBox, Pane, When } from "../ui";
import { useAsync } from "../useAsync";

/** In the order they escalate: seeing, then using the subscription, then changing what
 *  students gave, then reaching a student. */
const CAPS: { key: string; label: string; hint: string }[] = [
  { key: "can_view", label: "View",
    hint: "Open the cohort at all. Without it nothing else on the row has any effect — a "
        + "membership with View off is not a weaker view, it is no view." },
  { key: "can_run_grading", label: "Run grading",
    hint: "Press Run, and change what a run would cover. A run uses the Claude subscription "
        + "grading runs on — every cohort shares it — and reaches no student." },
  { key: "can_edit_rubrics", label: "Bind rubrics",
    hint: "Choose what an assignment is marked against. Binding archives every existing "
        + "grade for it and re-queues them." },
  { key: "can_edit_settings", label: "Edit settings",
    hint: "The grading configuration, the forms, and whether this cohort may be written to "
        + "in Classroom." },
  { key: "can_manage_teams", label: "Applicants & teams",
    hint: "Both forms and their answers, setting people aside, and forming teams. Requesting a "
        + "grouping spends a few cents at the gateway; publishing teams is Release, because "
        + "that is what reaches students." },
  { key: "can_release", label: "Release",
    hint: "Send a mark to a student in Google Classroom. This is the only capability here "
        + "that anybody outside this console can see the effect of." },
];

function Row({ t, courses, you, onChange }: {
  t: AdminTeacher;
  courses: { id: string; name: string; write_allowed: boolean }[];
  you: string;
  onChange: (fn: () => Promise<unknown>) => void;
}) {
  const isYou = t.email === you;
  return (
    <Card className={t.active ? "" : "dim"}>
      <CardHead
        title={t.display_name || t.email}
        desc={t.email}
        right={<>
          {isYou && <Badge tone="plain" title="You are signed in as this account.">you</Badge>}
          {t.active
            ? <Badge tone="ok" dot>active</Badge>
            : <Badge tone="stop" title="Every session of theirs ended, and they cannot sign
in again until this is turned back on.">deactivated</Badge>}
          <button className="btn"
                  /* 🔴 THE ONE CONTROL THAT CAN LOCK THE CONSOLE, and it is one row away from
                     the one that cannot. The server refuses it too; this is the affordance,
                     never the rule. */
                  disabled={isYou && t.active}
                  title={isYou && t.active
                    ? "You cannot deactivate your own account — every live session of yours "
                      + "would end on its next request, and reversing it means a commit and "
                      + "a deploy."
                    : t.active
                      ? "End every live session of theirs and stop them signing in."
                      : "Let them sign in again. Their cohorts are unchanged."}
                  onClick={() => onChange(() => setTeacherActive(t.id, !t.active))}>
            {t.active ? "Deactivate" : "Reactivate"}
          </button>
        </>} />
      <CardBody>
        <div className="tablewrap">
          <table className="tb">
            <thead>
              <tr>
                <th>Cohort</th>
                {CAPS.map((c) => (
                  <th key={c.key} className="num" title={c.hint}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => {
                const held = t.courses[c.id] ?? {};
                return (
                  <tr key={c.id}>
                    <td>
                      {c.name}
                      {/* ⚠️ NOT A CAPABILITY, AND SAYING SO IS THE POINT. Granting Release
                          on a read-only cohort still writes nothing to Classroom — the floor
                          is in code and no control on this page can reach it. Without this
                          the grid reads as though ticking a box reopened the cohort. */}
                      {!c.write_allowed && (
                        <> <Badge tone="stop" title="Nothing in this console may write a
grade, a release or an announcement to this cohort. Release still grants the button; the
cohort is what stops it reaching Google.">read-only</Badge></>
                      )}
                    </td>
                    {CAPS.map((cap) => (
                      <td key={cap.key} className="num">
                        <input type="checkbox"
                               aria-label={`${cap.label} on ${c.name} for ${t.email}`}
                               checked={!!held[cap.key]}
                               disabled={!t.active}
                               onChange={(e) => {
                                 const next = { ...held, [cap.key]: e.target.checked };
                                 onChange(() => setTeacherCapabilities(
                                   t.id, c.id,
                                   Object.fromEntries(
                                     CAPS.map((k) => [k.key, !!next[k.key]]))));
                               }} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          Added <When iso={t.created_at} />
          {t.last_seen_at ? <> · last signed in <When iso={t.last_seen_at} /></> : <> · never
            signed in</>}
        </p>
      </CardBody>
    </Card>
  );
}

export function TeachersPage() {
  const q = useAsync(() => getAdminTeachers(), []);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [said, setSaid] = useState("");

  const run = (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    fn().then(() => { q.reload(); })
      .catch((e: ApiError) => setErr(e))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <h2>Teachers</h2>
      {/* 🔴 PROSE, NOT A `Why` TOOLTIP — which is what this was, and the render is what caught
          it. `Why` is a `(i)` button that opens a popover on click, so the one sentence this
          page has to make ("adding somebody grants nothing") was a glyph with nothing beside
          it. A fact you have to discover is not a fact the page states. */}
      <p className="muted">
        Signing in with Google proves who somebody is; it does not grant access. A person is
        refused by name until they are on this list, whatever they teach in Google Classroom.
        Adding somebody here grants <strong>nothing</strong> — they can sign in and will see
        no cohorts until a box below is ticked.
      </p>

      {err && <ErrorBox err={err} />}

      <Card>
        <CardHead title="Add a teacher"
                  desc="They will be able to sign in with Google and see nothing until you
                        give them a cohort." />
        <CardBody>
          <div className="row">
            <input className="input grow" type="email" value={email} placeholder="name@turbin3.org"
                   aria-label="Email address"
                   onChange={(e) => { setEmail(e.target.value); setSaid(""); }} />
            <button className="btn primary" disabled={busy || !email.trim()}
                    onClick={() => {
                      const addr = email.trim();
                      run(() => addTeacher(addr).then(() => {
                        setEmail("");
                        setSaid(`${addr.toLowerCase()} can sign in. They have no cohorts yet.`);
                      }));
                    }}>
              Add
            </button>
          </div>
          {/* ⚠️ THE CONFIRMATION SAYS WHAT DID *NOT* HAPPEN. "Added" alone reads as "they can
              now use the console", which is the opposite of the state this leaves them in. */}
          {said && <p className="muted small" role="status">{said}</p>}
          <p className="muted small">
            The address has to be the one they sign in to Google with. It is stored
            lower-cased, which is how the sign-in lookup reads it.
          </p>
        </CardBody>
      </Card>

      {/* ⚠️ NO `empty`. `Pane` only renders one for an ARRAY payload, and this one is an
          object — passing a placeholder here would be a promise nothing can keep. There is
          also no empty case: you are reading this, so there is at least one teacher. */}
      <Pane q={q}>
        {(d) => <>
          {d.teachers.map((t) => (
            <Row key={t.id} t={t} courses={d.courses} you={d.you} onChange={run} />
          ))}
          <p className="muted small">
            {/* 🔴 THE DOOR THIS PAGE DOES NOT OPEN. It lowers the friction of granting access,
                deliberately, and leaves the friction of granting the power to grant access
                exactly where it was: a reviewed commit and a deploy. */}
            Who may open this page is set in the console&apos;s own configuration, not here —
            because it is the power to grant every capability above, on every cohort.
          </p>
        </>}
      </Pane>
    </>
  );
}

export default TeachersPage;
